import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import app from '../firebase';

const functions = getFunctions(app, 'us-central1');

// Connect to emulator in development
if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
    connectFunctionsEmulator(functions, 'localhost', 5001);
}

// Types
export interface GenerateImageParams {
    prompt: string;
    model?: string;
}

export interface GenerateImageResult {
    success: boolean;
    image: {
        mimeType: string;
        data: string; // Base64
    };
}

export interface ShopifyProduct {
    id: number;
    title: string;
    handle: string;
    vendor: string;
    product_type: string;
    status: string;
    body_html?: string;
    tags?: string | string[];
    images: Array<{
        id: number;
        position: number;
        src: string;
        alt: string | null;
    }>;
    variants: Array<{
        id: number;
        title: string;
        price: string;
        sku: string;
    }>;
}

export interface GetProductsParams {
    shopId: string;
}

export interface GetProductsResult {
    products: ShopifyProduct[];
    totalCount: number;
}

export interface UploadToShopifyParams {
    shopId: string;
    productId: string;
    imageBase64: string;
    filename?: string;
    altText?: string;
}

// API Functions

/**
 * Generate an image using Gemini AI
 */
export async function generateImage(params: GenerateImageParams): Promise<GenerateImageResult> {
    const generateImageFn = httpsCallable<GenerateImageParams, GenerateImageResult>(
        functions,
        'generateImage'
    );
    const result = await generateImageFn(params);
    return result.data;
}

/**
 * Get products from a connected Shopify store
 */
export async function getShopifyProducts(params: GetProductsParams): Promise<GetProductsResult> {
    const getProductsFn = httpsCallable<GetProductsParams, GetProductsResult>(
        functions,
        'getShopifyProducts'
    );
    const result = await getProductsFn(params);
    return result.data;
}

/**
 * Upload an image to a Shopify product
 */
export async function uploadImageToShopify(params: UploadToShopifyParams): Promise<{ success: boolean; image: any }> {
    const uploadFn = httpsCallable<UploadToShopifyParams, { success: boolean; image: any }>(
        functions,
        'uploadToShopify'
    );
    const result = await uploadFn(params);
    return result.data;
}

/**
 * Initiate Shopify OAuth flow (admin only)
 */
export async function initiateShopifyOAuth(shop: string, token: string): Promise<{ authUrl: string }> {
    const response = await fetch(`/api/shopify/auth?shop=${encodeURIComponent(shop)}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    if (!response.ok) {
        throw new Error('Failed to initiate OAuth');
    }

    return response.json();
}

/**
 * Test an API key to verify it's valid
 */
export async function testApiKey(params: { provider: 'gemini' | 'openai'; apiKey: string }): Promise<{ success: boolean; message: string }> {
    const testApiKeyFn = httpsCallable<{ provider: string; apiKey: string }, { success: boolean; message: string }>(
        functions,
        'testApiKey'
    );
    const result = await testApiKeyFn(params);
    return result.data;
}

export interface DeleteShopifyImageParams {
    shopId: string;
    productId: string;
    imageId: string;
}

/**
 * Delete an image from a Shopify product
 */
export async function deleteShopifyImage(params: DeleteShopifyImageParams): Promise<{ success: boolean }> {
    const deleteFn = httpsCallable<DeleteShopifyImageParams, { success: boolean }>(
        functions,
        'deleteShopifyImage'
    );
    const result = await deleteFn(params);
    return result.data;
}

export interface AvailableModel {
    id: string;
    displayName: string;
    description: string;
    type: 'text' | 'image';
    inputTokenLimit?: number;
    outputTokenLimit?: number;
    supportedMethods?: string[];
}

export interface ListModelsResult {
    success: boolean;
    provider: string;
    models: AvailableModel[];
}

/**
 * List available models from Gemini or OpenAI API
 */
export async function listAvailableModels(provider: 'gemini' | 'openai'): Promise<ListModelsResult> {
    const listModelsFn = httpsCallable<{ provider: string }, ListModelsResult>(
        functions,
        'listAvailableModels'
    );
    const result = await listModelsFn({ provider });
    return result.data;
}

// ============================================
// Product Cache Functions
// ============================================

export interface SyncProductsResult {
    success: boolean;
    syncedCount: number;
    deletedCount: number;
}

export interface CachedProductsResult {
    products: ShopifyProduct[];
    totalCount: number;
    lastSyncedAt: string | null;
    fromCache: boolean;
}

/**
 * Sync all products from Shopify to Firestore cache
 */
export async function syncShopProducts(shopId: string): Promise<SyncProductsResult> {
    const syncFn = httpsCallable<{ shopId: string }, SyncProductsResult>(
        functions,
        'syncShopProducts'
    );
    const result = await syncFn({ shopId });
    return result.data;
}

/**
 * Get products from Firestore cache (much faster)
 */
export async function getCachedProducts(shopId: string): Promise<CachedProductsResult> {
    const getFn = httpsCallable<{ shopId: string }, CachedProductsResult>(
        functions,
        'getCachedProducts'
    );
    const result = await getFn({ shopId });
    return result.data;
}

export interface RegisterWebhooksResult {
    success: boolean;
    registered: string[];
}

/**
 * Register Shopify webhooks for real-time sync
 */
export async function registerShopifyWebhooks(shopId: string): Promise<RegisterWebhooksResult> {
    const registerFn = httpsCallable<{ shopId: string }, RegisterWebhooksResult>(
        functions,
        'registerShopifyWebhooks'
    );
    const result = await registerFn({ shopId });
    return result.data;
}

